import React, { useState, useEffect } from 'react';
import Header from '../component/Header';
import BackgroundImage from '../images/tirvia/trivia-bg.png';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { FaCheckCircle } from "react-icons/fa";
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../utils/tokenHelper';

const QuickcareTrivia = () => {
    const [showModal, setShowModal] = useState(true);
    const [quizStarted, setQuizStarted] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [timeLeft, setTimeLeft] = useState(120);
    const [attemptsLeft, setAttemptsLeft] = useState(0);
    const [loading, setLoading] = useState(true);
    const [canAccess, setCanAccess] = useState(false);
    const [accessMessage, setAccessMessage] = useState('');
    const [quizConfig, setQuizConfig] = useState(null);
    const [batchId, setBatchId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [startingQuiz, setStartingQuiz] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [submittingAnswer, setSubmittingAnswer] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [completionMessage, setCompletionMessage] = useState('');
    const [showTimeoutModal, setShowTimeoutModal] = useState(false);
    const [timeExpired, setTimeExpired] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const fetchQuizConfig = async () => {
            // Add 5-second delay before fetching
            await new Promise(resolve => setTimeout(resolve, 5000));
            
            const token = getToken();
            if (!token) {
                console.error('No token found');
                setErrorMessage('Please login to play this game');
                setShowErrorModal(true);
                setTimeout(() => {
                    navigate('/trivia-games');
                }, 3000);
                setLoading(false);
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "GET",
                headers: myHeaders,
                redirect: "follow"
            };

            try {
                const response = await fetch("https://devapi.quickcare.ng/api/v1/games/mcq-like/config?game=quickcare", requestOptions);
                
                if (!response.ok) {
                    throw new Error(`Server error: ${response.status}`);
                }
                
                const result = await response.json();

                if (result.success && result.data) {
                    setQuizConfig(result.data);
                    setCanAccess(result.data.can_access || false);
                    setAccessMessage(result.data.message || '');

                    if (typeof result.data.attempts === 'number') {
                        setAttemptsLeft(result.data.attempts);
                    }

                    if (typeof result.data.max_winning_minutes === 'number') {
                        setTimeLeft(Math.floor(result.data.max_winning_minutes * 60));
                    }

                    if (typeof result.data.max_question === 'number') {
                        setTotalQuestions(result.data.max_question);
                    }
                } else {
                    throw new Error(result.message || 'Failed to load game configuration');
                }
            } catch (error) {
                console.error('Error fetching quiz config:', error);
                setErrorMessage(error.message || 'Failed to load game. Please try again later.');
                setShowErrorModal(true);
                setTimeout(() => {
                    navigate('/trivia-games');
                }, 3000);
            } finally {
                setLoading(false);
            }
        };

        fetchQuizConfig();
    }, [navigate]);

    useEffect(() => {
        if (quizStarted && timeLeft > 0 && !timeExpired) {
            const timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        setTimeExpired(true);
                        setShowTimeoutModal(true);
                        setQuizStarted(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [quizStarted, timeLeft, timeExpired]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAnswerSelect = (optionId) => {
        setSelectedAnswer(optionId);
    };

    const isFinalQuestion = () => {
        const lastIndex = totalQuestions ? totalQuestions - 1 : 3;
        return currentIndex >= lastIndex;
    };

    const hydrateQuestion = (payload) => {
        if (!payload) return null;
        return {
            id: payload.current_index + 1,
            question: payload.question_answers.question,
            options: [
                { id: 'A', text: payload.question_answers.a },
                { id: 'B', text: payload.question_answers.b },
                { id: 'C', text: payload.question_answers.c },
                { id: 'D', text: payload.question_answers.d }
            ]
        };
    };

    const submitAnswer = async () => {
        const token = getToken();
        if (!token) {
            console.error('No token found');
            return null;
        }

        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", `Bearer ${token}`);

        const raw = JSON.stringify({
            "batch_id": batchId,
            "answer": selectedAnswer.toLowerCase()
        });

        const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: raw,
            redirect: "follow"
        };

        const response = await fetch("https://devapi.quickcare.ng/api/v1/games/mcq-like/answer/quickcare", requestOptions);
        return response.json();
    };

    const handleNext = async () => {
        if (!selectedAnswer || !batchId || submittingAnswer) {
            return;
        }

        setSubmittingAnswer(true);

        try {
            const result = await submitAnswer();
            if (result?.success && result.data) {
                setCurrentIndex(result.data.current_index);
                const nextQuestion = hydrateQuestion(result.data);
                if (nextQuestion) {
                    setQuestions(prev => [...prev, nextQuestion]);
                    setCurrentQuestion(result.data.current_index);
                    setSelectedAnswer(null);
                }
            }
        } catch (error) {
            console.error('Error submitting answer:', error);
        } finally {
            setSubmittingAnswer(false);
        }
    };

    const handleFinish = async () => {
        if (!selectedAnswer || !batchId || submittingAnswer) {
            return;
        }

        setSubmittingAnswer(true);

        try {
            const result = await submitAnswer();
            if (result?.success) {
                setCompletionMessage(result.message || 'Quiz completed successfully!');
                setShowCompletionModal(true);
            }
        } catch (error) {
            console.error('Error submitting final answer:', error);
        } finally {
            setSubmittingAnswer(false);
        }
    };

    const handlePlay = async () => {
        if (!canAccess) {
            return;
        }

        setStartingQuiz(true);

        try {
            const token = getToken();
            if (!token) {
                console.error('No token found');
                setStartingQuiz(false);
                return;
            }

            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestOptions = {
                method: "POST",
                headers: myHeaders,
                redirect: "follow"
            };

            const response = await fetch("https://devapi.quickcare.ng/api/v1/games/mcq-like/start?game=quickcare", requestOptions);
            const result = await response.json();

            if (result.success && result.data) {
                setBatchId(result.data.batch_id);
                const firstQuestion = hydrateQuestion(result.data);
                if (firstQuestion) {
                    setQuestions([firstQuestion]);
                    setCurrentQuestion(0);
                    setCurrentIndex(result.data.current_index);
                }

                setShowModal(false);
                setQuizStarted(true);
            } else if (!result.success) {
                // Handle error response
                setErrorMessage(result.message || 'Failed to start quiz. Please try again.');
                setShowErrorModal(true);
            }
        } catch (error) {
            console.error('Error starting quiz:', error);
        } finally {
            setStartingQuiz(false);
        }
    };

    const handleCloseModal = () => {
        navigate('/trivia-games');
    };

    const handleCloseCompletionModal = () => {
        navigate('/trivia-games');
    };

    const handleTryAgain = () => {
        if (!canAccess) {
            navigate('/trivia-games');
            return;
        }

        setShowTimeoutModal(false);
        setTimeExpired(false);
        setQuestions([]);
        setCurrentQuestion(0);
        setCurrentIndex(0);
        setSelectedAnswer(null);
        setBatchId(null);
        setSubmittingAnswer(false);

        if (quizConfig && typeof quizConfig.max_winning_minutes === 'number') {
            setTimeLeft(Math.floor(quizConfig.max_winning_minutes * 60));
        }

        setShowModal(true);
        setQuizStarted(false);
    };

    const currentQuestionData = questions[currentQuestion] || null;

    return (
        <div className='w-full flex items-start justify-center bg-[#F9FAFB] relative'>
            <div className='flex flex-col w-full '>
                <div>
                    <Header 
                        title='QuickCare Trivia'
                    />  
                </div>

                <div 
                    className='min-h-screen p-4 bg-cover bg-center bg-no-repeat flex items-center justify-center'
                    style={{ backgroundImage: `url(${BackgroundImage})` }}
                >
                    {showModal ? (
                        <div className='w-full max-w-xl px-4'>
                            <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                                <button
                                    onClick={handleCloseModal}
                                    className='absolute top-4 right-4 text-white hover:text-gray-300 transition-colors'
                                >
                                    <IoClose className='text-2xl' />
                                </button>

                                <div className='text-center mb-6 mt-4'>
                                    <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                        QuickCare Trivia
                                    </h1>
                                </div>

                                <div className='mb-4'>
                                    <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                        Tackle rapid-fire QuickCare questions and aim for a perfect score before the timer runs out.
                                    </p>
                                </div>

                                {accessMessage && (
                                    <div className='mb-4'>
                                        <p className='text-white text-center text-xs md:text-sm instrument-sans-medium'>
                                            {accessMessage}
                                        </p>
                                    </div>
                                )}

                                {loading ? (
                                    <div className='mb-8'>
                                        <div className='flex flex-col items-center gap-2'>
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                            <p className='text-white text-center text-sm md:text-base instrument-sans-semibold'>
                                                Loading game configuration...
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className='mb-8'>
                                        <p className='text-white text-center text-sm md:text-base instrument-sans-semibold'>
                                            Note: You have {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} left.
                                        </p>
                                    </div>
                                )}

                                <div className='flex justify-center'>
                                    <button
                                        onClick={handlePlay}
                                        disabled={!canAccess || loading || startingQuiz}
                                        className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full disabled:opacity-50 disabled:cursor-not-allowed'
                                    >
                                        {startingQuiz ? 'Starting...' : loading ? 'Loading...' : 'Play'}
                                    </button>
                                </div>

                                {showErrorModal && (
                                    <div className='mt-4'>
                                        <p className='text-red-300 text-center text-sm md:text-base instrument-sans-normal mb-2'>
                                            {errorMessage}
                                        </p>
                                        <p className='text-white/70 text-center text-xs instrument-sans-normal'>
                                            Redirecting to games page...
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : !showCompletionModal && !showTimeoutModal ? (
                        currentQuestionData ? (
                            <div className='w-full max-w-xl px-4'>
                                <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl'>
                                    <div className='flex flex-col items-center mb-6'>
                                        <div className='flex items-center gap-2 text-white mb-2'>
                                            <AiOutlineClockCircle className='text-xl' />
                                            <span className='text-base md:text-lg instrument-sans-bold'>
                                                {formatTime(timeLeft)}
                                            </span>
                                        </div>
                                        {batchId && (
                                            <p className='text-white/60 text-xs'>
                                                Batch ID: {batchId}
                                            </p>
                                        )}
                                    </div>

                                    <div className='mb-8 flex flex-col items-center gap-2'>
                                        <p className='text-base md:text-lg instrument-sans-bold text-white'>
                                            Question {currentQuestion + 1}/{totalQuestions || questions.length}
                                        </p>

                                        <h2 className='text-white text-lg md:text-[24px] text-center instrument-sans-bold'>
                                            {currentQuestionData.question}
                                        </h2>
                                    </div>

                                    <div className='space-y-4 mb-6'>
                                        {currentQuestionData.options.map((option) => {
                                            const isSelected = selectedAnswer === option.id;
                                            
                                            return (
                                                <button
                                                    key={option.id}
                                                    onClick={() => handleAnswerSelect(option.id)}
                                                    className={`w-full text-left p-4 rounded-lg border transition-all duration-200 ${
                                                        isSelected
                                                            ? 'bg-white text-[#000] border-white'
                                                            : 'bg-transparent text-white border-[#ABBAAF] '
                                                    }`}
                                                >
                                                    <div className='flex items-center justify-between'>
                                                        <span className='instrument-sans-medium'>
                                                            {option.id}. {option.text}
                                                        </span>
                                                        {isSelected && (
                                                            <FaCheckCircle className='text-[#069125] text-xl' />
                                                        )}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    <div className='flex justify-end'>
                                        {isFinalQuestion() ? (
                                            <button
                                                onClick={handleFinish}
                                                disabled={!selectedAnswer || submittingAnswer}
                                                className='bg-[#069125] hover:bg-[#057a1f] text-white instrument-sans-medium py-3 px-8 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                                            >
                                                {submittingAnswer ? 'Submitting...' : 'Finish and Submit'}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleNext}
                                                disabled={!selectedAnswer || submittingAnswer}
                                                className='bg-[#069125] hover:bg-[#057a1f] text-white instrument-sans-medium py-3 px-8 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                                            >
                                                {submittingAnswer ? 'Loading...' : 'Next Question'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className='w-full max-w-xl px-4'>
                                <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl text-center text-white'>
                                    <p>Loading question...</p>
                                </div>
                            </div>
                        )
                    ) : null}
                </div>
            </div>

            {showCompletionModal && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                    <div className='w-full max-w-xl px-4'>
                        <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                            <div className='text-center mb-6 mt-4'>
                                <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                    Quiz Completed!
                                </h1>
                            </div>

                            <div className='mb-8'>
                                <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                    {completionMessage}
                                </p>
                            </div>

                            <div className='flex justify-center'>
                                <button
                                    onClick={handleCloseCompletionModal}
                                    className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full'
                                >
                                    Back to Trivia Games
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showTimeoutModal && (
                <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
                    <div className='w-full max-w-xl px-4'>
                        <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                            <div className='text-center mb-6 mt-4'>
                                <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                                    Time's Up!
                                </h1>
                            </div>

                            <div className='mb-8'>
                                <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                                    Comiseration. you did not complete in time.
                                </p>
                            </div>

                            <div className='flex justify-center'>
                                <button
                                    onClick={handleTryAgain}
                                    disabled={!canAccess}
                                    className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full disabled:opacity-50 disabled:cursor-not-allowed'
                                >
                                    {canAccess ? 'Try Again' : 'Back to Trivia Games'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickcareTrivia;


